/**
 * Admin section catalog — the discovery surface for every admin
 * feature, shipped or unbuilt. Drives:
 *
 *   1. `AdminPage` (`/admin` index) — a responsive grid of iconed
 *      cards, one per section. Shipped sections are clickable;
 *      unbuilt sections show with a "Coming soon" badge and a
 *      dimmed icon. Sections that have a `children` array render
 *      with a quick-list of their sub-page labels under the
 *      description, so the discovery surface still telegraphs the
 *      tree shape even though the click target is the parent.
 *
 *   2. `AdminPlaceholder` (`/admin/<segment>` catch-all) — looks the
 *      pathname up here to render section-aware copy ("Audit Log —
 *      Coming soon") instead of a generic "Not built yet." The
 *      lookup walks parent + children so an unbuilt child path also
 *      gets section-aware copy.
 *
 * Adding a new admin section:
 *   - Add an entry below with `status: 'unbuilt'` and the relevant
 *     `todo` field pointing at the issue/wave.
 *   - When the real screen ships, flip the entry to `status: 'shipped'`
 *     (DON'T delete it — the index page still wants to advertise the
 *     shipped surface as a discovery affordance).
 *   - Register the real route in `App.tsx` ABOVE the
 *     `<Route path="*" element={<AdminPlaceholder/>}/>` catch-all
 *     (React Router matches more-specific first).
 *
 * Sections with sub-pages:
 *   - Add a `children` array of `{ label, path, description }` —
 *     each child is itself a route under the parent's path.
 *   - The parent's `path` is the landing URL the discovery card
 *     navigates to; `App.tsx` should redirect that URL to the
 *     most-visited child (or render a small landing page if the
 *     parent has standalone content).
 *
 * Icons are `lucide-react` components — same library the Sidebar
 * uses, so the visual language stays consistent across the app.
 */
import {
  Sliders,
  LineChart,
  ScrollText,
  Building2,
  Sparkles,
  HardDrive,
  KeyRound,
  ShieldCheck,
  Users,
  Mail,
  CalendarClock,
  type LucideIcon,
} from 'lucide-react'

/** A child of a parent section — surfaces in the discovery card as
 * a quick-list under the description and in the in-page sub-nav
 * the parent renders on every child route. Children don't carry
 * their own icon / status — they inherit the parent's. */
export interface AdminSectionChild {
  label: string
  /** Absolute path under /admin/*. */
  path: string
  description: string
}

/** Which group a section sits under in the admin rail / grid (#1528).
 * Ordering of the groups themselves lives in `ADMIN_SECTION_GROUPS`. */
export type AdminSectionGroup = 'customers' | 'access' | 'insights' | 'operations'

/** The groups, in display order — the rail's group headers + their
 * order. `ADMIN_SECTIONS` below is ordered to match (each group's
 * members are contiguous and in their intended in-group order), so the
 * rail and the `/admin` discovery grid both surface sections in this
 * order. Ordered by how often an operator reaches for them. */
export const ADMIN_SECTION_GROUPS: ReadonlyArray<{
  id: AdminSectionGroup
  label: string
  /** One-line description of the group — rendered as the rail group
   * header's tooltip (not a visible subhead). */
  sub: string
}> = [
  { id: 'customers', label: 'Customers', sub: 'tenants, people & entitlements' },
  { id: 'access', label: 'Access & Governance', sub: 'who can act & what they did' },
  { id: 'insights', label: 'Insights', sub: 'reporting' },
  { id: 'operations', label: 'Operations', sub: 'AI config & system' },
]

export interface AdminSection {
  label: string
  /** The rail/grid group this section belongs to (#1528). */
  group: AdminSectionGroup
  /** Absolute path under /admin/*. For sections with children this
   * is the landing URL — `App.tsx` typically redirects it to the
   * first child. */
  path: string
  description: string
  /** Lucide icon component. Same source the Sidebar uses for nav
   * entries, so the iconography stays consistent across surfaces. */
  icon: LucideIcon
  /** Tailwind text-color class for the icon — matches the Sidebar's
   * per-entry brand colors so a section's icon looks the same in
   * both the index grid and (if it ever gets a sidebar entry) the
   * sidebar. */
  iconColor: string
  /** `'shipped'` — the real route exists, card is clickable.
   * `'unbuilt'` — placeholder; card is shown dimmed with a "Coming
   * soon" badge and is non-interactive. */
  status: 'shipped' | 'unbuilt'
  /** Issue/wave tracker. Surfaced on unbuilt cards so a reviewer
   * sees what's pending without grep'ing the codebase. */
  todo?: string
  /** Optional sub-pages. Present when this section is a parent of a
   * sub-nav (today: Agents → Prompts/Models/Pricing/Budgets/RAG). Children
   * render as a small quick-list under the parent's discovery
   * card. */
  children?: readonly AdminSectionChild[]
}

export const ADMIN_SECTIONS: readonly AdminSection[] = [
  {
    label: 'Organizations',
    group: 'customers',
    path: '/admin/orgs',
    description: 'Provision new tenants and review existing orgs with owner + workspace count.',
    icon: Building2,
    iconColor: 'text-sky-400',
    status: 'shipped',
  },
  {
    label: 'Users',
    group: 'customers',
    path: '/admin/users',
    description:
      'Global directory of every signed-up user, with filters (orphan, sign-in recency, provider) and per-user membership-graph detail. Cross-tenant by design — the read trail is audited.',
    icon: Users,
    iconColor: 'text-cyan-400',
    status: 'shipped',
  },
  {
    label: 'Invitations',
    group: 'customers',
    path: '/admin/invitations',
    description:
      'Cross-org invitations directory: every pending, accepted, and expired invitation across every organization, filterable by status, org, and free-text invitee + inviter email patterns. Audited on read.',
    icon: Mail,
    iconColor: 'text-teal-400',
    status: 'shipped',
  },
  {
    label: 'Admins',
    group: 'access',
    path: '/admin/admins',
    description:
      'Who has full access to this installation: the Global Admins, and the automation tokens that can only start test runs and read results. Every change is recorded in the audit log.',
    icon: ShieldCheck,
    iconColor: 'text-fuchsia-400',
    status: 'shipped',
    children: [
      {
        label: 'People',
        path: '/admin/admins',
        description:
          'Make someone a Global Admin, or remove them. Every change is recorded in the audit log.',
      },
      {
        label: 'Automation Tokens',
        path: '/admin/admins/operator-tokens',
        description:
          'Create and revoke tokens for tools outside the app. They can start test runs and read results, nothing else.',
      },
    ],
  },
  {
    label: 'Audit Log',
    group: 'access',
    path: '/admin/audit',
    description: 'A record of admin actions: membership, credentials, organization changes and Global Admin grants.',
    icon: ScrollText,
    iconColor: 'text-amber-400',
    status: 'shipped',
  },
  {
    label: 'Analytics',
    group: 'insights',
    path: '/admin/analytics',
    description: 'Cross-org BI aggregates: cost, tokens, tool usage, quality, model comparison.',
    icon: LineChart,
    iconColor: 'text-pink-400',
    status: 'shipped',
  },
  {
    label: 'AI Models',
    group: 'operations',
    path: '/admin/models',
    description:
      'Per-agent model assignment with each model\'s live health (continuous checks + per-row "Check now") on one screen, the embedding model, prompt management, the pricing card, run budgets, and Agent Memory tools.',
    icon: Sparkles,
    iconColor: 'text-violet-400',
    status: 'shipped',
    children: [
      {
        label: 'Prompts',
        path: '/admin/models/prompts',
        description:
          'Runtime-managed agent prompts: list, version, compare, restore, tag, comment. Edits land instantly without a deploy.',
      },
      {
        label: 'Assignments',
        path: '/admin/models/assignment',
        description:
          'Per-agent provider+model+temperature defaults, with each model\'s live health and a "Check now" button on the same row. The embedding model shows as a read-only health row.',
      },
      {
        label: 'Model catalog',
        path: '/admin/models/catalog',
        description:
          'Every model we know about — token rates, what it can do (tool calling, reasoning, vision, context size), and where each fact came from. This is the list the per-agent model dropdown is sourced from.',
      },
      {
        label: 'Budgets',
        path: '/admin/models/budgets',
        description:
          'App-wide fallback iteration caps for the agent runners — per test case, per script, per heal.',
      },
      {
        label: 'Agent Memory',
        path: '/admin/models/rag',
        description:
          'Agent Memory retrieval inspector: compare top-K, dense, lexical, and hybrid side-by-side for any (variant, agent, query). Forcing function for the hybrid-promotion decision.',
      },
    ],
  },
  {
    label: 'Scheduled Jobs',
    group: 'operations',
    path: '/admin/scheduled-jobs',
    description:
      'Catalog of recurring background jobs — when each ran, what it did, when it runs next. Pause, resume, retime, or trigger an immediate run.',
    icon: CalendarClock,
    iconColor: 'text-lime-400',
    status: 'shipped',
  },
  {
    label: 'Runtime Config',
    group: 'operations',
    path: '/admin/config',
    description: 'Platform feature flags and email sender configuration.',
    icon: Sliders,
    iconColor: 'text-indigo-400',
    status: 'shipped',
  },
  {
    label: 'Artifacts',
    group: 'operations',
    path: '/admin/artifacts',
    description: 'Run-artifact retention: bucket lifecycle + backend serving cap, with live bucket statistics.',
    icon: HardDrive,
    iconColor: 'text-orange-400',
    status: 'shipped',
  },
  {
    label: 'System Fixtures',
    group: 'operations',
    path: '/admin/system-fixtures',
    description:
      'Read-only catalog of the pre-canned Playwright login fixtures the planner can auto-match. Surfaces the full spec template, URL patterns, and required credential fields for each entry.',
    icon: KeyRound,
    iconColor: 'text-rose-400',
    status: 'shipped',
  },

]

/** Look up an entry by its `/admin/<segment>` path. Used by
 * `AdminPlaceholder` to render context-aware copy when a user
 * direct-navigates to an unbuilt section. Walks BOTH parent paths
 * and child paths — a request for `/admin/agents/budgets` resolves
 * to the Budgets child entry (or the Agents parent if you query
 * `/admin/agents` itself). Returns `undefined` for unknown paths;
 * the placeholder falls back to a generic message in that case. */
export function findAdminSectionByPath(
  pathname: string,
): AdminSection | AdminSectionChild | undefined {
  for (const s of ADMIN_SECTIONS) {
    if (s.path === pathname) return s
    if (s.children) {
      const child = s.children.find((c) => c.path === pathname)
      if (child) return child
    }
  }
  return undefined
}
